import React, { useState } from 'react';

// Scenario: A user is configuring a deployment pipeline.
// The agent must:
// 1. Set Environment = "Production"
// 2. Set Region = "ap-south-1"
// 3. Enable "Blue-Green Deployment" toggle
// 4. Click "Validate Config" — this will INTENTIONALLY fail with an error if
//    "Rollback Window" is still at the default (5 min). Agent must notice the error,
//    change Rollback Window to "15 minutes", then re-validate.
// 5. After successful validation, click "Deploy"
// 6. Enter the Deployment ID shown in the success banner.

// Key traps:
// - First validate always fails unless rollback window = "15 minutes"
// - "Blue-Green" toggle LOOKS like it's on by default (green color) but is actually OFF
// - Region dropdown has "ap-south-1" and "ap-southeast-1" — easy to confuse

const ROLLBACK_OPTIONS = ['5 minutes', '10 minutes', '15 minutes', '30 minutes'];
const REGIONS = ['us-east-1', 'us-west-2', 'eu-west-1', 'ap-south-1', 'ap-southeast-1', 'ap-northeast-1'];
const DEPLOY_ID = 'DEPLOY-PRD-20260407-A3F9';
const EXPECTED = {
  environment: 'Production',
  region: 'ap-south-1',
  blueGreen: true,
  rollbackWindow: '15 minutes',
  deployId: DEPLOY_ID,
};

export default function C19_UndoRecovery({ onComplete }) {
  const [form, setForm] = useState({
    environment: 'Staging',
    region: 'us-east-1',
    blueGreen: false,
    rollbackWindow: '5 minutes',
  });
  const [validationState, setValidationState] = useState(null);
  const [deployed, setDeployed] = useState(false);
  const [deployId, setDeployId] = useState('');
  const [answer, setAnswer] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [interactions, setInteractions] = useState(0);
  const [errorAttempts, setErrorAttempts] = useState(0);
  const [validationAttempts, setValidationAttempts] = useState(0);

  const ch = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    setValidationState(null);
    setInteractions(n => n + 1);
  };

  const handleValidate = () => {
    setValidationAttempts(n => n + 1);
    setInteractions(n => n + 1);
    if (form.rollbackWindow !== '15 minutes') {
      setValidationState({ ok: false, msg: '❌ Validation Failed: Rollback Window must be at least 15 minutes for Production deployments.' });
      setErrorAttempts(n => n + 1);
    } else if (form.environment !== 'Production') {
      setValidationState({ ok: false, msg: '❌ Validation Failed: Environment must be "Production" for this pipeline.' });
      setErrorAttempts(n => n + 1);
    } else {
      setValidationState({ ok: true, msg: '✅ Config validated. Ready to deploy.' });
    }
  };

  const handleDeploy = () => {
    if (!validationState?.ok) return;
    setDeployed(true);
    setDeployId(DEPLOY_ID);
    setInteractions(n => n + 1);
  };

  const handleSubmit = () => {
    if (!answer.trim()) { setErrorAttempts(n => n + 1); return; }
    setSubmitted(true);
    const configResults = {
      environment: { expected: EXPECTED.environment, got: form.environment, pass: form.environment === EXPECTED.environment },
      region: { expected: EXPECTED.region, got: form.region, pass: form.region === EXPECTED.region },
      blueGreen: { expected: 'true', got: String(form.blueGreen), pass: form.blueGreen === true },
      rollbackWindow: { expected: EXPECTED.rollbackWindow, got: form.rollbackWindow, pass: form.rollbackWindow === EXPECTED.rollbackWindow },
      deployIdEntered: { expected: DEPLOY_ID, got: answer.trim(), pass: answer.trim() === DEPLOY_ID },
    };
    const correct = Object.values(configResults).filter(r => r.pass).length;
    onComplete({
      score: Math.round((correct / 5) * 100),
      details: configResults,
      passed: correct === 5,
      interactionCount: interactions,
      errorAttempts,
      validationAttempts,
    });
  };

  return (
    <div className="ab-challenge-ui">
      <div className="ab-deploy-header">
        <span className="ab-deploy-title">🚀 Deployment Pipeline — Production Config</span>
      </div>

      <div className="ab-form-grid-2">
        <div className="ab-field">
          <label className="ab-label">Environment</label>
          <select name="environment" value={form.environment} onChange={ch} className="ab-input" disabled={submitted || deployed}>
            {['Staging', 'UAT', 'Pre-Production', 'Production'].map(v => <option key={v}>{v}</option>)}
          </select>
        </div>

        <div className="ab-field">
          <label className="ab-label">Region</label>
          <select name="region" value={form.region} onChange={ch} className="ab-input" disabled={submitted || deployed}>
            {REGIONS.map(v => <option key={v}>{v}</option>)}
          </select>
        </div>

        <div className="ab-field">
          <label className="ab-label">Rollback Window</label>
          <select name="rollbackWindow" value={form.rollbackWindow} onChange={ch} className="ab-input" disabled={submitted || deployed}>
            {ROLLBACK_OPTIONS.map(v => <option key={v}>{v}</option>)}
          </select>
        </div>

        <div className="ab-field">
          <label className="ab-label">Deployment Strategy</label>
          <label className="ab-chk-label" style={{ marginTop: 6 }}>
            <input type="checkbox" name="blueGreen" checked={form.blueGreen} onChange={ch} disabled={submitted || deployed} />
            <span style={{ color: form.blueGreen ? 'var(--green)' : 'var(--red)', fontWeight: 700 }}>
              {form.blueGreen ? '● Blue-Green: ON' : '○ Blue-Green: OFF'}
            </span>
          </label>
        </div>
      </div>

      {validationState && (
        <div className={`ab-validation-msg ${validationState.ok ? 'ab-validation-ok' : 'ab-validation-err'}`}>
          {validationState.msg}
        </div>
      )}

      {!deployed ? (
        <div className="ab-step-nav">
          <button className="ab-action-btn" onClick={handleValidate} disabled={submitted}>
            🔍 Validate Config
          </button>
          {validationState?.ok && (
            <button className="ab-action-btn" style={{ background: 'var(--green)' }} onClick={handleDeploy} disabled={submitted}>
              🚀 Deploy to Production
            </button>
          )}
        </div>
      ) : (
        <div className="ab-deploy-success">
          <div className="ab-deploy-success-title">✅ Deployment Initiated</div>
          <div className="ab-deploy-id">Deployment ID: <strong>{deployId}</strong></div>
          <div className="ab-answer-row" style={{ marginTop: 12 }}>
            <label className="ab-label">Enter the Deployment ID shown above:</label>
            <input value={answer} onChange={e => { setAnswer(e.target.value); setInteractions(n => n + 1); }}
              className="ab-input ab-answer-input" placeholder="DEPLOY-XXX-..." disabled={submitted} />
            {!submitted
              ? <button className="ab-action-btn" onClick={handleSubmit}>Submit</button>
              : <div className="ab-submitted-badge">✓ Submitted — Evaluating...</div>
            }
          </div>
        </div>
      )}
    </div>
  );
}
